/**
 * /shadow-ai-seats — Okta/Rippling-aggregated AI SaaS audit.
 */
import React, { useEffect, useState } from 'react';
import { Users, AlertTriangle, TrendingDown, Upload } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import BrandTitle from '../components/BrandTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShadowAISeats = () => {
  const [audit, setAudit] = useState(null);
  const [savings, setSavings] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const headers = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
  });

  const load = async () => {
    setLoading(true);
    try {
      const [a, s, c] = await Promise.all([
        fetch(`${API}/shadow-ai/audit`, { headers: headers() }).then((r) => r.json()),
        fetch(`${API}/shadow-ai/savings-summary`, { headers: headers() }).then((r) => r.json()),
        fetch(`${API}/shadow-ai/catalog`).then((r) => r.json()),
      ]);
      setAudit(a);
      setSavings(s);
      setCatalog(c.vendors || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const seed = async () => {
    setSeeding(true);
    // Build a realistic Okta-style payload from the catalog
    const rows = catalog.map((c, idx) => ({
      vendor: c.vendor,
      seat_count: c.enterprise_breakpoint_seats + (idx === 0 ? 25 : idx === 1 ? 0 : Math.floor(Math.random() * 15) - 5),
      active_seats_30d: Math.floor(c.enterprise_breakpoint_seats * (0.4 + Math.random() * 0.4)),
      source: idx % 2 === 0 ? 'okta' : 'rippling',
    }));
    await fetch(`${API}/shadow-ai/ingest`, { method: 'POST', headers: headers(), body: JSON.stringify({ rows }) });
    await load();
    setSeeding(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="shadow-ai-page">
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <Badge className="bg-amber-100 border-amber-300 text-amber-300 px-3 py-1 text-[11px] tracking-[0.15em] uppercase font-semibold mb-3" data-testid="shadow-ai-badge">
              <Users className="w-3 h-3 mr-1.5 inline" /> Shadow AI · Okta/Rippling
            </Badge>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight"><BrandTitle>Shadow AI Seat Audit</BrandTitle></h1>
            <p className="text-slate-300 mt-1 max-w-2xl">
              In 2026 every employee has 10+ "AI seat" subscriptions on corporate cards. CARTIE aggregates them via your Okta or Rippling directory and finds the consolidation savings.
            </p>
          </div>
          <Button onClick={seed} disabled={seeding} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950" data-testid="shadow-ai-seed">
            <Upload className="w-4 h-4 mr-1.5" /> {seeding ? 'Loading sample…' : 'Load sample directory'}
          </Button>
        </div>

        {savings && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8" data-testid="shadow-ai-headline">
            <Card className="bg-emerald-500/10 border-emerald-500/30 shadow-sm">
              <CardContent className="p-5">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">Total monthly waste</div>
                <div className="text-3xl font-extrabold text-emerald-300 tabular-nums">${savings.total_monthly_savings_usd?.toLocaleString() || 0}</div>
                <div className="text-xs text-slate-300 mt-1">consolidation + idle seat reclaim</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-white/10 shadow-sm">
              <CardContent className="p-5">
                <div className="text-[10px] uppercase tracking-widest text-slate-300 font-bold">Annual savings</div>
                <div className="text-3xl font-extrabold text-white tabular-nums">${savings.annual_savings_usd?.toLocaleString() || 0}</div>
                <div className="text-xs text-slate-300 mt-1">12× the monthly opportunity</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-white/10 shadow-sm">
              <CardContent className="p-5">
                <div className="text-[10px] uppercase tracking-widest text-slate-300 font-bold">Vendors eligible</div>
                <div className="text-3xl font-extrabold text-white tabular-nums">{savings.eligible_vendors || 0}</div>
                <div className="text-xs text-slate-300 mt-1">at the enterprise breakpoint</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Audit table */}
        <Card className="bg-slate-900/60 border-white/10 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-white/10">
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              AI seats by vendor
            </h2>
          </div>
          {(!audit?.rows || audit.rows.length === 0) ? (
            <div className="px-6 py-12 text-center text-slate-500">
              <Users className="w-10 h-10 mx-auto text-slate-400 mb-2" />
              <p>No directory data yet — click <strong>Load sample directory</strong> above to see a populated audit.</p>
              <p className="text-xs mt-3">Production: connect your Okta or Rippling tenant via SCIM in <code>Admin → Integrations</code>.</p>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="shadow-ai-table">
              <thead className="bg-slate-900/60 border-b border-white/10">
                <tr>
                  <th className="text-left px-6 py-3 font-semibold text-slate-300">Vendor</th>
                  <th className="text-right px-3 py-3 font-semibold text-slate-300">Seats</th>
                  <th className="text-right px-3 py-3 font-semibold text-slate-300">Active 30d</th>
                  <th className="text-right px-3 py-3 font-semibold text-slate-300">Idle</th>
                  <th className="text-right px-3 py-3 font-semibold text-slate-300">$ / mo</th>
                  <th className="text-right px-3 py-3 font-semibold text-slate-300">Idle waste</th>
                  <th className="text-right px-6 py-3 font-semibold text-slate-300">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {audit.rows.map((r, i) => (
                  <tr key={r.id || i} className="hover:bg-slate-900/60" data-testid={`shadow-ai-row-${r.vendor}`}>
                    <td className="px-6 py-3 font-semibold text-white">{r.label}</td>
                    <td className="px-3 py-3 text-right text-slate-200 tabular-nums">{r.seat_count}</td>
                    <td className="px-3 py-3 text-right text-slate-200 tabular-nums">{r.active_seats_30d}</td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      <span className={r.idle_seats >= 10 ? 'text-rose-600 font-semibold' : 'text-slate-200'}>{r.idle_seats}</span>
                    </td>
                    <td className="px-3 py-3 text-right text-white tabular-nums">${r.monthly_spend_usd.toLocaleString()}</td>
                    <td className="px-3 py-3 text-right text-rose-600 tabular-nums font-semibold">${r.idle_waste_usd.toLocaleString()}</td>
                    <td className="px-6 py-3 text-right">
                      {r.consolidate_recommended ? (
                        <Badge className="bg-emerald-100 border-emerald-300 text-emerald-300 text-xs">Consolidate →</Badge>
                      ) : r.idle_seats > 0 ? (
                        <Badge className="bg-amber-100 border-amber-300 text-amber-300 text-xs">Reclaim idle</Badge>
                      ) : (
                        <Badge className="bg-slate-900/60 border-white/15 text-slate-300 text-xs">Healthy</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <p className="text-xs text-slate-500 mt-4 italic">
          Consolidation savings assume a 25% enterprise-tier discount — a conservative published-rate floor. Idle-seat savings reclaim any seat with zero logins in trailing 30 days.
        </p>
      </main>
    </div>
  );
};

export default ShadowAISeats;
